/**
 * CreateArtifactDialog (TASK-746) — ONE create surface for every entry point.
 *
 * Type (Document / Spreadsheet / Presentation) × starting point (Generate from
 * description / Blank / Template / Upload):
 *  - Blank + Upload stay DIRECT POSTs (~200ms) and open the editor — that is
 *    the obvious intent; an LLM round-trip for a blank file is never added.
 *  - Template renders server-side (docx only today — the option hides for
 *    other types) and lands as an in-dialog ArtifactCard (TASK-742) — review
 *    before committing attention, never a forced navigation.
 *  - Generate goes through the AGENT: the dialog prefills the active dock/rail
 *    input with a well-formed instruction; the result arrives as an
 *    ArtifactCard (TASK-742), never a navigation.
 *
 * Context (client/task in scope at the mount) is a visible, removable chip —
 * never silently attached. Failures keep the form open with input intact.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet,
  FileText,
  LayoutTemplate,
  Plus,
  Presentation,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  createFromTemplate,
  createWorkbenchDocument,
  listDocumentTemplates,
  type DocumentTemplate,
} from '../../services/api/documentApi';
import { clientService, type Client } from '../../services/clientService';
import { ArtifactCard } from '../canvas/widgets/ArtifactCard';
// TASK-785: one builder for the `?task=` scope, shared with every workbench opener.
import { workbenchRoute } from '../../utils/workbenchRoute';

export type CreateArtifactType = 'docx' | 'xlsx' | 'pptx';
type StartingPoint = 'generate' | 'blank' | 'template' | 'upload';

export interface CreateArtifactDialogProps {
  open: boolean;
  onClose: () => void;
  defaultType?: CreateArtifactType;
  /** Context prefill — shown as a removable chip, never silently attached. */
  contextClientId?: string | null;
  contextClientName?: string | null;
  contextTaskId?: string | null;
  /**
   * Non-navigation results (template): the mount reacts (refresh a list, show
   * a card). Blank/Upload always open the editor.
   */
  onCreated?: (result: { document_id: string; version: number; format: string }) => void;
}

const TYPE_META: Record<CreateArtifactType, { label: string; icon: React.ReactNode; noun: string }> = {
  docx: { label: 'Document', icon: <FileText className="h-4 w-4" />, noun: 'document' },
  xlsx: { label: 'Spreadsheet', icon: <FileSpreadsheet className="h-4 w-4" />, noun: 'spreadsheet' },
  pptx: { label: 'Presentation', icon: <Presentation className="h-4 w-4" />, noun: 'presentation' },
};

const BLANK_TITLES: Record<CreateArtifactType, string> = {
  docx: 'Untitled document',
  xlsx: 'Untitled sheet',
  pptx: 'Untitled deck',
};

export const CreateArtifactDialog: React.FC<CreateArtifactDialogProps> = ({
  open,
  onClose,
  defaultType = 'docx',
  contextClientId = null,
  contextClientName = null,
  contextTaskId = null,
  onCreated,
}) => {
  const navigate = useNavigate();
  const [type, setType] = useState<CreateArtifactType>(defaultType);
  const [start, setStart] = useState<StartingPoint>('generate');
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [templates, setTemplates] = useState<DocumentTemplate[] | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<string>('');
  const [clientChipRemoved, setClientChipRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // TASK-742/746: a non-navigation result (template) renders as an in-dialog
  // ArtifactCard — review before committing attention.
  const [created, setCreated] = useState<{
    document_id: string;
    version: number;
    format: string;
    title: string | null;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset per open; context chip re-arms.
  useEffect(() => {
    if (!open) return;
    setType(defaultType);
    setStart('generate');
    setError(null);
    setTitle('');
    setCreated(null);
    setClientChipRemoved(false);
    setClientId(contextClientId ?? '');
  }, [open, defaultType, contextClientId]);

  // Lazy data: templates + clients on first open.
  useEffect(() => {
    if (!open || templates !== null) return;
    listDocumentTemplates()
      .then((res) => {
        setTemplates(res.templates);
        setSelectedTemplate(res.templates[0]?.id ?? null);
      })
      .catch(() => setTemplates([]));
    clientService
      .fetchClients(0, 100)
      .then((res) => setClients(res.clients))
      .catch(() => setClients([]));
  }, [open, templates]);

  // Templates are docx-only today — hide the option rather than show an empty picker.
  const templatesForType = type === 'docx' ? (templates ?? []) : [];
  const templateAvailable = templatesForType.length > 0;
  useEffect(() => {
    if (start === 'template' && !templateAvailable) setStart('generate');
  }, [start, templateAvailable]);

  const chipClient =
    !clientChipRemoved && contextClientId
      ? {
          id: contextClientId,
          // m7: resolve a bare id through the fetched list — never render a UUID.
          name:
            contextClientName ??
            clients.find((c) => c.id === contextClientId)?.name ??
            contextClientId,
        }
      : null;
  const effectiveClientId = chipClient ? chipClient.id : clientId || undefined;

  const finishOpen = useCallback(
    (documentId: string) => {
      onClose();
      navigate(workbenchRoute(documentId, contextTaskId));
    },
    [navigate, onClose, contextTaskId],
  );

  const handleBlank = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await createWorkbenchDocument({
        format: type,
        title: title.trim() || BLANK_TITLES[type],
        clientId: effectiveClientId,
      });
      finishOpen(res.document_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the document.');
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const res = await createWorkbenchDocument({ file, clientId: effectiveClientId });
      toast.success(`Uploaded ${file.name}`);
      finishOpen(res.document_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleTemplate = async () => {
    if (!selectedTemplate) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createFromTemplate({
        template_id: selectedTemplate,
        client_id: effectiveClientId,
        ...(title.trim() ? { title: title.trim() } : {}),
      });
      toast.success('Document created from the template.');
      onCreated?.(res);
      // The result lands as an ArtifactCard IN the dialog — not a navigation,
      // not a silent close (TASK-742 three-mounts rule applies here too).
      setCreated({
        ...res,
        title: templatesForType.find((t) => t.id === selectedTemplate)?.title ?? null,
      });
    } catch (err) {
      // The form stays open with input intact — never silently reset.
      setError(err instanceof Error ? err.message : 'Could not create from the template.');
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = () => {
    const text = description.trim();
    if (!text) return;
    const noun = TYPE_META[type].noun;
    const clientPart = chipClient
      ? ` for client ${chipClient.name}`
      : effectiveClientId
        ? ` for client id ${effectiveClientId}`
        : '';
    // M4 (epic review): the chip promises task context — the instruction
    // carries it so the agent's auto-attach binds the artifact to the task.
    const taskPart = contextTaskId ? ` (bound to task ${contextTaskId})` : '';
    const message = `Create a ${noun}${clientPart}${taskPart}: ${text}`;
    // Generate goes through the AGENT (only it can author the content spec).
    // Whichever conversation surface is live picks the prefill up; the user
    // reviews the instruction and sends it — creation lands as an ArtifactCard.
    window.dispatchEvent(new CustomEvent('kielis:dock-prefill', { detail: { text: message } }));
    window.dispatchEvent(new CustomEvent('kielis:rail-prefill', { detail: { text: message } }));
    toast.success('Instruction placed in the assistant input — press send to generate.');
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:items-center"
      data-testid="create-artifact-dialog"
      onMouseDown={(e) => {
        // m5: backdrop click closes (the panel stops propagation below).
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Create an artifact"
        className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Create</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {created ? (
          // Result state: the created artifact as the shared card (Open /
          // Add to task / Export) — the RM reviews it before going anywhere.
          <div data-testid="create-result-card">
            <ArtifactCard
              documentId={created.document_id}
              title={created.title}
              format={created.format}
              version={created.version}
              clientId={effectiveClientId ?? null}
            />
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
        {/* Context chips — visible and removable, never silent. */}
        {(chipClient || contextTaskId) && (
          <div className="mb-3 flex flex-wrap gap-1.5" data-testid="context-chips">
            {chipClient && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800">
                Client: {chipClient.name}
                <button
                  type="button"
                  aria-label="Remove client context"
                  onClick={() => {
                    // Removing the chip removes the context entirely — the
                    // select must not silently re-attach the same client.
                    setClientChipRemoved(true);
                    setClientId('');
                  }}
                  className="hover:text-blue-950"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {contextTaskId && (
              <span className="inline-flex items-center gap-1 rounded-full bg-kielis-navy/10 px-2 py-0.5 text-xs text-kielis-navy">
                Opens with task context
              </span>
            )}
          </div>
        )}

        {/* Type */}
        <div className="mb-3 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Artifact type">
          {(Object.keys(TYPE_META) as CreateArtifactType[]).map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={type === t}
              onClick={() => setType(t)}
              className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-sm ${
                type === t
                  ? 'border-kielis-navy bg-kielis-navy/5 font-medium text-kielis-navy'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {TYPE_META[t].icon}
              {TYPE_META[t].label}
            </button>
          ))}
        </div>

        {/* Starting point */}
        <div className="mb-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Starting point">
          {(
            [
              ['generate', 'Generate from description', <Sparkles key="g" className="h-3.5 w-3.5" />],
              ['blank', 'Blank', <Plus key="b" className="h-3.5 w-3.5" />],
              ...(templateAvailable
                ? ([["template", 'Template', <LayoutTemplate key="t" className="h-3.5 w-3.5" />]] as const)
                : []),
              ['upload', 'Upload', <Upload key="u" className="h-3.5 w-3.5" />],
            ] as [StartingPoint, string, React.ReactNode][]
          ).map(([point, label, icon]) => (
            <button
              key={point}
              type="button"
              role="radio"
              aria-checked={start === point}
              onClick={() => setStart(point)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
                start === point
                  ? 'border-kielis-navy bg-kielis-navy text-white'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {icon}
              {label}
            </button>
          ))}
        </div>

        {/* Body per starting point */}
        {start === 'generate' && (
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder={`Describe the ${TYPE_META[type].noun} — the assistant creates it and you review the result`}
            aria-label="Describe what to generate"
            className="mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        )}

        {(start === 'blank' || start === 'template') && (
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="create-title">
              Title (optional)
            </label>
            <input
              id="create-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={BLANK_TITLES[type]}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        )}

        {start === 'template' && templateAvailable && (
          <div className="mb-3 max-h-52 space-y-2 overflow-y-auto" data-testid="template-options">
            {templatesForType.map((t) => (
              <label
                key={t.id}
                className={`flex cursor-pointer items-start gap-2 rounded-md border p-2.5 ${
                  selectedTemplate === t.id
                    ? 'border-kielis-navy bg-blue-50/50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="create-template"
                  checked={selectedTemplate === t.id}
                  onChange={() => setSelectedTemplate(t.id)}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">{t.title}</span>
                  <span className="block text-xs text-gray-500">{t.description}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        {/* Client picker (when no context chip pins one) */}
        {!chipClient && (
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="create-client">
              Client (optional)
            </label>
            <select
              id="create-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">No client</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <p data-testid="create-error" className="mb-3 text-xs text-red-700">
            {error}
          </p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".docx,.xlsx,.pptx,.pdf"
          className="hidden"
          data-testid="create-upload-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleUpload(f);
            e.target.value = '';
          }}
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="create-submit"
            disabled={
              busy ||
              (start === 'generate' && description.trim() === '') ||
              (start === 'template' && !selectedTemplate)
            }
            onClick={() => {
              if (start === 'generate') handleGenerate();
              else if (start === 'blank') void handleBlank();
              else if (start === 'template') void handleTemplate();
              else fileInputRef.current?.click();
            }}
            className="rounded-md bg-kielis-navy px-3 py-1.5 text-sm font-semibold text-white hover:bg-kielis-navy/90 disabled:opacity-50"
          >
            {busy
              ? 'Creating…'
              : start === 'generate'
                ? 'Generate'
                : start === 'upload'
                  ? 'Choose file…'
                  : 'Create'}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateArtifactDialog;
